import { createSelector } from 'reselect';
import { matchPath } from 'react-router';
import { parseLocationSearch } from 'utils';

export const selectRouterInitialLocation = (store) => store.router.initialLocation;
export const selectRouterLocation = (store) => store.router.location;
export const selectRouterPreviousLocation = (store) => store.router.previousLocation;
export const selectRouterQuery = (store) => store.router.query;
export const selectRouterGeolocation = (store) => store.router.geolocation;
export const selectGclidDependenciesReady = (store) => store.router.gclidDependenciesReady;
// export const selectStoreIdReadingReady = (store) => store.router.storeIdReadingReady;

/**
 * @deprecated for performance and clarity, consider using selectRouterMatch instead
 */
export const routerMatchPath = createSelector(
  [(_, path) => path, selectRouterLocation],
  (path, { pathname }) => matchPath(pathname, { path }),
);

export const selectInitialQueryStringsParams = createSelector(
  [selectRouterInitialLocation],
  (initialLocation) => parseLocationSearch(initialLocation),
);

export const hasEpoi = createSelector([selectInitialQueryStringsParams], (params) => {
  return Boolean(params?.epoi) || Boolean(params?.sf_epoi);
});

export const selectStoreId = createSelector(
  [selectInitialQueryStringsParams],
  (params) => params?.storeId || params?.sf_store_id,
);
export const hasStoreId = createSelector([selectStoreId], (storeId) => Boolean(storeId));

export const selectRetailerId = createSelector([selectInitialQueryStringsParams], (params) => {
  const parsed = Number(params?.retailerId ?? params?.sf_retailer_id);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : null;
});

export const selectCustomLabel = createSelector(
  [selectInitialQueryStringsParams],
  (params) => params?.customLabel,
);
export const hasCustomLabel = createSelector([selectCustomLabel], (customLabel) =>
  Boolean(customLabel),
);

/**
 * Smart Viewer init `hide_download` (forwarded as `hideDownload=true`), e.g. viewer opened inside a retailer app webview.
 * When true, the PDF download button is hidden regardless of the property `enable_download` extra.
 */
export const selectHideDownload = createSelector(
  [selectInitialQueryStringsParams],
  (params) => params?.hideDownload === 'true',
);

const VALID_DEFAULT_FLYER_VERSIONS = new Set(['PDF', 'NEXT']);

/**
 * Preferred format on flyer open (Smart Viewer init / URL: `defaultFlyerVersion` or `default_flyer_version`).
 * Returns `null` when the param is **absent** or not exactly `PDF` or `NEXT` (case-insensitive) — no implicit default;
 * realignment to a default version only runs when an integration passes this param explicitly.
 * @returns {'PDF'|'NEXT'|null}
 */
export const selectDefaultFlyerVersion = createSelector(
  [selectInitialQueryStringsParams],
  (params) => {
    const raw = params?.defaultFlyerVersion ?? params?.default_flyer_version;
    if (raw == null || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const u = trimmed.toUpperCase();
    return VALID_DEFAULT_FLYER_VERSIONS.has(u) ? u : null;
  },
);
